package com.devi3east.bashbuddy.dashboard.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ConfirmationNumber
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Pause
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Tune
import androidx.compose.material3.Badge
import androidx.compose.material3.BadgedBox
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.features.common.util.formatPrice
import com.devi3east.bashbuddy.features.events.common.data.models.TicketStatus
import com.devi3east.bashbuddy.features.events.common.data.models.TicketType

@Composable
fun DashboardTicketCard(
    ticketId: String,
    ticketType: TicketType,
    status: TicketStatus,
    title: String,
    revenue: Long,
    ticketsSold: Int,
    maxQuantity: Int,
    pendingAssignments: Int,
    onEdit: (String) -> Unit,
    onDelete: (String) -> Unit,
    onAdjust: (String) -> Unit,
    onTogglePause: (String, Boolean) -> Unit,
    onAssignClick: () -> Unit
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 4.dp),
        shape = RoundedCornerShape(16.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 4.dp),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surface,
            contentColor = MaterialTheme.colorScheme.onSurface
        ),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.onBackground.copy(alpha = 0.1f))
    ) {
        Box(modifier = Modifier.fillMaxWidth()) {

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 16.dp)
            ) {
                // Title and revenue row
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = title,
                        style = MaterialTheme.typography.titleSmall,
                        modifier = Modifier.weight(1f)
                    )
                    Text(
                        text = formatPrice(revenue),
                        style = MaterialTheme.typography.titleSmall
                    )
                }

                Spacer(modifier = Modifier.height(8.dp))

                // Progress indicator
                val progress = if (maxQuantity > 0) ticketsSold / maxQuantity.toFloat() else 0f
                LinearProgressIndicator(
                    progress = { progress },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(8.dp),
                    color = MaterialTheme.colorScheme.primary,
                    trackColor = MaterialTheme.colorScheme.surfaceVariant
                )

                Spacer(modifier = Modifier.height(8.dp))

                // Tickets sold text
                Text(
                    text = "$ticketsSold / $maxQuantity tickets sold",
                    style = MaterialTheme.typography.labelSmall,
                    modifier = Modifier.align(Alignment.CenterHorizontally)
                )

                Spacer(modifier = Modifier.height(8.dp))

                // Bottom row of icons (5 slots)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceEvenly,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Slot 1: Delete
                    IconButton(
                        onClick = { onDelete(ticketId) },
                        modifier = Modifier.background(
                            color = MaterialTheme.colorScheme.error.copy(alpha = 0.1f),
                            shape = RoundedCornerShape(8.dp)
                        )
                    ) {
                        Icon(
                            imageVector = Icons.Default.Delete,
                            contentDescription = "Delete Ticket",
                            tint = MaterialTheme.colorScheme.error
                        )
                    }

                    // Slot 2: Edit
                    IconButton(
                        onClick = { onEdit(ticketId) },
                        modifier = Modifier.background(
                            color = MaterialTheme.colorScheme.primary.copy(alpha = 0.1f),
                            shape = RoundedCornerShape(8.dp)
                        )
                    ) {
                        Icon(
                            imageVector = Icons.Default.Edit,
                            contentDescription = "Edit Ticket",
                            tint = MaterialTheme.colorScheme.primary
                        )
                    }

                    BadgedBox(
                        badge = {
                            if (pendingAssignments > 0) {
                                Badge { Text(pendingAssignments.toString()) }
                            }
                        }
                    ) {
                        IconButton(
                            onClick = { onAssignClick() },
                            modifier = Modifier.background(
                                color = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.1f),
                                shape = RoundedCornerShape(8.dp)
                            )
                        ) {
                            Icon(
                                imageVector = Icons.Default.ConfirmationNumber,
                                contentDescription = "Assign Printed Tickets",
                                tint = MaterialTheme.colorScheme.tertiary
                            )
                        }
                    }

                    // Slot 4: Pause/Play
                    IconButton(
                        onClick = { onTogglePause(ticketId, status != TicketStatus.PAUSED) },
                        modifier = Modifier.background(
                            color = MaterialTheme.colorScheme.primary.copy(alpha = 0.1f),
                            shape = RoundedCornerShape(8.dp)
                        )
                    ) {
                        Icon(
                            imageVector = if (status == TicketStatus.PAUSED) Icons.Default.PlayArrow else Icons.Default.Pause,
                            contentDescription = if (status == TicketStatus.PAUSED) "Resume Ticket" else "Pause Ticket",
                            tint = MaterialTheme.colorScheme.primary
                        )
                    }

                    // Slot 5: Adjust (printed only)
                    if (ticketType == TicketType.PRINTED) {
                        IconButton(
                            onClick = { onAdjust(ticketId) },
                            modifier = Modifier.background(
                                color = MaterialTheme.colorScheme.secondary.copy(alpha = 0.1f),
                                shape = RoundedCornerShape(8.dp)
                            )
                        ) {
                            Icon(
                                imageVector = Icons.Default.Tune,
                                contentDescription = "Adjust Ticket Quantity",
                                tint = MaterialTheme.colorScheme.secondary
                            )
                        }
                    } else {
                        // Placeholder for digital ticket
                        Spacer(modifier = Modifier.width(48.dp))
                    }
                }
            }

            // Paused overlay
            if (status == TicketStatus.PAUSED) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .fillMaxHeight()
                        .background(
                            color = MaterialTheme.colorScheme.error.copy(alpha = 0.1f),
                            shape = RoundedCornerShape(16.dp)
                        ),
                    contentAlignment = Alignment.Center
                ) {
                    Box(
                        modifier = Modifier
                            .background(
                                color = MaterialTheme.colorScheme.errorContainer,
                                shape = RoundedCornerShape(16.dp)
                            )
                            .padding(horizontal = 16.dp, vertical = 12.dp)
                    ) {
                        Text(
                            text = "TICKET SALES\nDEACTIVATED",
                            color = MaterialTheme.colorScheme.onErrorContainer,
                            style = MaterialTheme.typography.titleMedium,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }
    }
}